import React, { useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"

import { useAppDispatch, useAppSelector } from "redux/hooks"
import { selectContent, closeDialog } from "redux/features/dialog/dialogSlice"
import { fetchPaymentSchedule } from "redux/features/payment_schedule/paymentScheduleThunk"
import {
  selectFetchStatus,
  selectPaymentScheduleById,
} from "redux/features/payment_schedule/paymentScheduleSlice"
import { Progress } from "components/ui/Spinner"
import Close from "components/ui/ModalClose"
import LoadableEntity from "components/ui/LoadableEntity"
import {
  LoaderContainer,
  ErrorContainer,
  Headline,
  Title,
  Description,
} from "hocs/features/modal/elements"
import { PaymentScheduleType } from "types"

// NOTE: hoc API
/* fetch mode
  content: {
    debtId: 1,
    paymentScheduleId: 2,
  }

  – Debt will be found in redux by debtId
  – PS will be fetched from the backend by paymentScheduleId
*/

/* render mode
  content: {
    debtId: 1,
    paymentSchedule: {
      id: 2,
      ...
    },
  }

  – Debt will be found in redux by debtId
  – PS will be rendered as it is
 */

export function withModalPaymentSchedule<GenericType>(
  WrappedComponent: React.ComponentType<GenericType>,
) {
  const WithModalPaymentSchedule = (props: GenericType) => {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const { t } = useTranslation()

    const content = useAppSelector((state) => selectContent(state))
    const { paymentScheduleId, paymentSchedule } = content
    const payment_schedule = paymentScheduleId
      ? useAppSelector(
          (state) =>
            selectPaymentScheduleById(
              state,
              paymentScheduleId,
            ) as PaymentScheduleType,
        )
      : paymentSchedule || {}

    const fetchStatus = useAppSelector((state) => selectFetchStatus(state))

    useEffect(() => {
      const handleFetchPaymentSchedule = () => {
        if (!fetchStatus.isFetching && paymentScheduleId) {
          dispatch(
            fetchPaymentSchedule({ payment_schedule_id: paymentScheduleId }),
          )
        }
      }

      handleFetchPaymentSchedule()
    }, [paymentScheduleId])

    const handleClose = () => {
      dispatch(closeDialog())
      navigate("/")
    }

    const renderLoader = () => {
      return (
        <LoaderContainer>
          <Progress color="primary" />
        </LoaderContainer>
      )
    }

    const renderError = () => {
      return (
        <ErrorContainer>
          <Close onClick={handleClose} />
          <Headline>
            <Title variant="h3">
              {t("features.payment_schedule.loading.title")}
            </Title>
            <Title variant="h5">
              {t("features.payment_schedule.loading.error.title")}
            </Title>
            <Description variant="h6">
              {t("features.payment_schedule.loading.error.description")}
            </Description>
          </Headline>
        </ErrorContainer>
      )
    }

    return (
      <LoadableEntity
        entity={payment_schedule}
        fetchStatus={fetchStatus}
        renderLoading={renderLoader}
        renderError={renderError}
      >
        {(payment_schedule) => (
          <WrappedComponent
            {...props}
            payment_schedule={payment_schedule}
            fetchStatus={fetchStatus}
          />
        )}
      </LoadableEntity>
    )
  }

  return WithModalPaymentSchedule
}
